<template>
  <label class="bui-ter-search">
    <input
      ref="inputRef"
      class="input"
      type="search"
      :value="modelValue"
      :placeholder="placeholder"
      :aria-label="ariaLabel || placeholder"
      :data-testid="dataTestId"
      autocomplete="off"
      spellcheck="false"
      @input="onInput"
      @keydown="onKeydown"
    />
    <span class="ico" aria-hidden="true">
      <PhMagnifyingGlass :size="24" weight="regular" />
    </span>
  </label>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { PhMagnifyingGlass } from '@phosphor-icons/vue';

withDefaults(
  defineProps<{
    modelValue?: string;
    placeholder?: string;
    ariaLabel?: string;
    dataTestId?: string;
  }>(),
  { modelValue: '', placeholder: 'Filter by Search...', dataTestId: 'list-search' },
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
  enter: [value: string];
}>();

const inputRef = ref<HTMLInputElement | null>(null);

function onInput(e: Event) {
  emit('update:modelValue', (e.target as HTMLInputElement).value);
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') {
    emit('enter', (e.target as HTMLInputElement).value);
  }
}

defineExpose({
  focus: () => inputRef.value?.focus(),
});
</script>

<style lang="scss" scoped>
.bui-ter-search {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 328px;
  max-width: 100%;
  height: var(--bui-ter-h, 52px);
  background: var(--bui-ter-input-fill, rgba(42, 41, 105, 0.12));
  border: 1px solid var(--bui-ter-input-border, rgba(42, 41, 105, 0.04));
  border-radius: var(--bui-ter-radius, 8px);
  padding: 0 14px;
  box-sizing: border-box;
  flex-shrink: 0;
  transition: background-color 120ms ease;
}

.input {
  flex: 1 1 auto;
  min-width: 0;
  background: transparent;
  border: 0;
  outline: 0;
  font-family: inherit;
  font-size: 14px;
  line-height: 17px;
  color: var(--bui-ter-input-text, #fffcfd);
  height: 100%;

  &::placeholder {
    color: var(--bui-ter-input-hint, #ffb2db);
    opacity: 1;
  }

  &::-webkit-search-cancel-button,
  &::-webkit-search-decoration {
    -webkit-appearance: none;
  }
}

.ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--bui-ter-icon, #fffcfd);
  margin-left: 8px;
  flex-shrink: 0;
  line-height: 0;

  :deep(svg) {
    width: 24px;
    height: 24px;
    display: block;
  }
}

.bui-ter-search:focus-within {
  background: var(--bui-ter-fill-hover, rgba(246, 247, 255, 0.16));
}

@media (max-width: 768px) {
  .bui-ter-search {
    padding: 0 10px;
  }

  .input {
    font-size: 13px;
    line-height: 16px;
  }

  .ico {
    margin-left: 6px;
  }

  .ico :deep(svg) {
    width: 18px;
    height: 18px;
  }
}
</style>
